import { type InfiniteData, useInfiniteQuery, type UseInfiniteQueryResult } from '@tanstack/react-query';
import type { GetFindingsWithCountQueryParams } from 'api/ApiDefinition';
import { QUERY } from 'api/Query';
import { QUERY_CLIENT } from 'api/QueryClient';
import type { ServiceCallError } from 'api/ServiceCallError';
import type { PaginatedFindingsWithCount } from 'typedefs/PaginatedFindingsWithCount';

/** Returns the React query definition used to load the endless-scroll list of findings with the given parameters. */
export function useInfiniteFindingsQuery(
	projectId: string,
	findingsQueryParams: Omit<GetFindingsWithCountQueryParams, 'start'>
): UseInfiniteQueryResult<InfiniteData<PaginatedFindingsWithCount>, ServiceCallError> {
	return useInfiniteQuery({
		queryKey: ['findings', projectId, findingsQueryParams],
		queryFn({ pageParam, signal }) {
			return QUERY.getFindingsWithCount(projectId, { ...findingsQueryParams, start: pageParam }).fetch({
				signal
			});
		},
		initialPageParam: 0,
		throwOnError: false,
		staleTime: 1000,
		getNextPageParam(lastPage: PaginatedFindingsWithCount): number | undefined {
			if (lastPage.resultSize <= lastPage.nextStartIndex!) {
				return undefined;
			}
			return lastPage.nextStartIndex!;
		}
	});
}

/** Invalidates the infinite query used for loading the findings in the findings list view. */
export function invalidateFindingsList() {
	void QUERY_CLIENT.invalidateQueries({ queryKey: ['findings'] });
}
